import type { QuestionVisibilityState } from "@/components/survey/Question";
import {
  DiabetesTypeOptionId,
  type DisqualificationReason,
  HealthConditionOptionId,
  NoomMedMoreOptionsSelection,
  PregnantSelection,
} from "@/constants";
import { STATE_FARM_PARTNERS } from "@/constants/Partners";
import {
  shouldShowGlp1MedicationQuestion,
  shouldShowGlp1WeightQuestion,
} from "@/utils/survey/glp1";

import { getAge, getDateOfBirth } from "../age";
import { calculateBMIImperial } from "../bmi";
import { isGlp1FdaLabelingEnforced } from "./glp1EligibilityConfig";
import { getMedBmiThresholds, type MedBmiThresholds } from "./medBmiConfig";

const ELIGIBLE_COMORBIDITIES = [
  HealthConditionOptionId.ABNORMAL_CHOLESTEROL,
  HealthConditionOptionId.HEART_DISEASE,
  HealthConditionOptionId.HIGH_BLOOD_PRESSURE,
  HealthConditionOptionId.KIDNEY_DISEASE,
  HealthConditionOptionId.LIVER_DISEASE,
  HealthConditionOptionId.LOW_TESTOSTERONE,
  HealthConditionOptionId.OSTEOARTHRITIS,
  HealthConditionOptionId.PCOS_OR_FERTILITY_PROBLEMS,
  HealthConditionOptionId.SLEEP_PROBLEMS,
  HealthConditionOptionId.TYPE2_DIABETES,
];

/**
 * Returns whether or not a user would qualify for Med based on their BMI and
 * health conditions
 *
 * The thresholds come from the partner (defaults 30 / 27). A user would not
 * qualify if
 * - BMI < minBmiWithComorbidity or
 * - BMI is between minBmiWithComorbidity and minBmi and user does not have an
 *   eligible comorbodity
 *
 * @param healthConditions the user's answers to the health conditions question
 * @param heightFeetPortion the feet portion of the user's height
 * @param heightInchesPortion the inches portion of the user's height
 * @param weight the user's weight
 * @param thresholds the partner's Med BMI thresholds
 * @returns true if the user would qualify for Med, false otherwise
 */
export const meetsMedBmiAndComorbityCriteria = (
  diabetesType: DiabetesTypeOptionId | undefined,
  healthConditions: string[] | undefined,
  heightFeetPortion: number,
  heightInchesPortion: number,
  weight: number,
  thresholds: MedBmiThresholds,
) => {
  const hasEligibleComorbidity =
    !!healthConditions?.some((hc) =>
      ELIGIBLE_COMORBIDITIES.includes(hc as HealthConditionOptionId),
    ) ||
    [DiabetesTypeOptionId.TYPE2, DiabetesTypeOptionId.UNSURE].includes(
      diabetesType as DiabetesTypeOptionId,
    );

  const bmi = calculateBMIImperial(
    heightFeetPortion,
    heightInchesPortion,
    weight,
  );

  return (
    bmi >= thresholds.minBmi ||
    (bmi >= thresholds.minBmiWithComorbidity && hasEligibleComorbidity)
  );
};

/**
 * This function returns whether a user qualifies for Noom Med based on the
 * current visibility state. This is used to determine question visibility
 * so it does mean it will return null if some questions are not answered
 * (e.g. if a user does not answer the pregnant question - user is male, or has
 * not answered the state of residency question yet)
 *
 * @param {QuestionVisibilityState} state The entire visibility state
 * @returns {DisqualificationReason | null} Returns a disqualification reason
 * if the user does not qualify for Med, null if the user qualifies
 */
export const qualifiesForNoomMed = (
  state: QuestionVisibilityState,
): DisqualificationReason | null => {
  const {
    eligibilityInfo: { eligiblePrograms = [] } = {},
    partnerInfo,
    retriageEligibility,
    surveyAnswers,
    telehealthEligibleStates,
  } = state;

  const {
    diabetesDiagnosis,
    diabetesType,
    eatingDisorder,
    glp1Medication,
    glp1Weight,
    healthConditions,
    heightWeight: { heightFeetPortion, heightInchesPortion, weight } = {},
    liabilityWaiverBMI,
    liabilityWaiverEatingDisorder,
    liabilityWaiverPregnant,
    locationState,
    noomMedMoreOptions,
    pregnant,
  } = surveyAnswers;

  const { eligibleRetriagePrograms = [] } = retriageEligibility || {};
  const userEligiblePrograms = retriageEligibility
    ? eligibleRetriagePrograms
    : eligiblePrograms;

  // This is a safety check but in theory if eligible programs doesn't include
  // Med this qualification function won't even run
  if (!userEligiblePrograms.includes("MED")) {
    return "PROGRAM_NOT_OFFERED";
  }

  const dob = getDateOfBirth({ surveyAnswers, retriageEligibility });
  const age = getAge({ surveyAnswers, retriageEligibility });

  if (!dob) {
    return "NO_DOB_FOUND";
  }

  if (age !== undefined && age > 80) {
    return "AGE_TOO_HIGH";
  }

  if (eatingDisorder === "yes") {
    return "EATING_DISORDER";
  }

  if (
    liabilityWaiverBMI ||
    liabilityWaiverEatingDisorder ||
    liabilityWaiverPregnant
  ) {
    return "LIABILITY_WAIVER_SIGNED";
  }

  const hasAnsweredHeightWeightQuestion =
    heightFeetPortion !== undefined &&
    heightInchesPortion !== undefined &&
    weight !== undefined;

  // Users with BMI < 18.5 should be immediately screened out for Med
  if (hasAnsweredHeightWeightQuestion) {
    const currentBmi = calculateBMIImperial(
      heightFeetPortion,
      heightInchesPortion,
      weight,
    );

    if (currentBmi < 18.5) {
      return "BMI_TOO_LOW";
    }
  }

  if (
    noomMedMoreOptions &&
    noomMedMoreOptions !== NoomMedMoreOptionsSelection.MED
  ) {
    return "USER_DECLINED_MED";
  }

  /**
   * https://noomhq.atlassian.net/browse/B2B-2158
   *
   * State Farm users qualify for Noom Med powered by Accolade and bypass
   * qualification criteria for BMI/comorbidity, type 1 diabetes, pregnancy,
   * or location if they are taking or have a prescription for a GLP-1. The
   * bypass only applies to initial enrollment, not retriage.
   */
  const isStateFarmGlp1Bypass =
    !retriageEligibility &&
    STATE_FARM_PARTNERS.includes(partnerInfo?.id as number) &&
    (!glp1Medication ||
      glp1Medication === "yes" ||
      glp1Medication === "prescriptionNotYetStarted");

  if (isStateFarmGlp1Bypass) {
    return null;
  }

  const hasAnsweredComorbidityQuestions =
    healthConditions !== undefined &&
    (diabetesDiagnosis === "no" ||
      (diabetesDiagnosis === "yes" && diabetesType !== undefined));

  // Pregnancy is a hard exclusion for Med (except for State Farm)
  if (pregnant && pregnant !== PregnantSelection.NO) {
    return "PREGNANT";
  }

  // Type 1 Diabetes is a hard exclusion for Med (except for State Farm)
  if (
    healthConditions?.includes(HealthConditionOptionId.TYPE1_DIABETES) ||
    diabetesType === DiabetesTypeOptionId.TYPE1
  ) {
    return "INELIGIBLE_DIABETES_TYPE";
  }

  if (
    locationState &&
    !(telehealthEligibleStates || []).includes(locationState)
  ) {
    return "MED_INELIGIBLE_LOCATION";
  }

  const hasCompletedGlp1Questions =
    (glp1Medication !== undefined ||
      !shouldShowGlp1MedicationQuestion(state)) &&
    (glp1Weight !== undefined || !shouldShowGlp1WeightQuestion(state));

  if (
    hasAnsweredHeightWeightQuestion &&
    hasAnsweredComorbidityQuestions &&
    hasCompletedGlp1Questions
  ) {
    // PAM-108: when FDA-labeling enforcement is off, current GLP-1 users skip
    // starting-weight / 5% and current Med BMI threshold gates (still after 18.5).
    const skipFdaLabelingExclusion =
      glp1Medication === "yes" && !isGlp1FdaLabelingEnforced(partnerInfo);

    if (skipFdaLabelingExclusion) {
      console.info("fda_labeling_exclusion_skipped", {
        partnerId: partnerInfo?.id,
      });
    } else {
      /**
       * If a user answers the GLP-1 weight question, they must
       * meet both of the following criteria to be eligible for Med:
       *
       * - The user must meet the partner's Med BMI thresholds (default 30+, or 27+ with comorbidity) when starting GLP-1
       * - The user must have lost at least 5% body weight
       *
       * https://www.figma.com/design/FAbvdVlekwEEUoVF9QvVx2/CareFirst%3A-Existing-GLP-1-users-who-don't-meed-med-criteria?node-id=2-15287&t=zAjFCAdal2WOeCoW-0
       */
      if (
        glp1Weight !== undefined &&
        !(
          meetsMedBmiAndComorbityCriteria(
            diabetesType,
            healthConditions,
            heightFeetPortion,
            heightInchesPortion,
            glp1Weight,
            getMedBmiThresholds(partnerInfo),
          ) && (glp1Weight - weight) / glp1Weight >= 0.05
        )
      ) {
        return "GLP_1_WEIGHT_CRITERIA_NOT_MET";
      }

      // If the user has not answered the GLP-1 weight question, they must meet the BMI and health conditions criteria
      // based on their current height and weight.
      if (
        glp1Weight === undefined &&
        !meetsMedBmiAndComorbityCriteria(
          diabetesType,
          healthConditions,
          heightFeetPortion,
          heightInchesPortion,
          weight,
          getMedBmiThresholds(partnerInfo),
        )
      ) {
        return "HEALTH_CONDITIONS_CRITERIA_NOT_MET";
      }
    }
  }
  return null;
};
